import { authRoutes, type LoginBody, type Me } from '@pickpro/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isApiError } from '../api/api-error';
import { callRoute, callSessionRoute } from '../api/client';
import { assertCurrentSession } from '../api/session-generation';
import { LIVE_BACKSTOP_INTERVAL } from '../live/live-refetch';
import { mirrorPreferences } from './mirror-preferences';
import { clearSessionCache, ME_QUERY_KEY, replaceSessionCache } from './session-cache';
import { publishSessionChange } from './session-channel';
import { hasSessionHint } from './session-hint';

export { ME_QUERY_KEY } from './session-cache';

export const fetchMe = async (signal?: AbortSignal): Promise<Me | null> => {
  try {
    return await callRoute(authRoutes.me, { signal });
  } catch (error) {
    if (
      isApiError(error) &&
      error.status === 401 &&
      error.problem.messageKey === 'errors.unauthorized'
    )
      return null;
    throw error;
  }
};

export const useSession = () => {
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => (hasSessionHint() ? fetchMe(signal) : null),
    enabled: typeof window !== 'undefined',
    staleTime: 30_000,
    retry: false,
    refetchInterval: (current) => (current.state.data ? LIVE_BACKSTOP_INTERVAL : false),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (current) => (current.state.data ? 'always' : false),
  });
  useEffect(() => {
    if (query.data) mirrorPreferences(query.data.settings);
  }, [query.data]);
  return {
    user: query.data ?? null,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
};

export const useSignIn = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: LoginBody) => {
      const { data, generation } = await callSessionRoute(authRoutes.login, { body });
      publishSessionChange({ type: 'signed-in', user: data });
      await replaceSessionCache(client, data, generation);
      assertCurrentSession(generation);
      return data;
    },
  });
};

export const useSignOut = (onSuccess?: (generation: number) => void | Promise<unknown>) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, generation } = await callSessionRoute(authRoutes.logout);
      publishSessionChange({ type: 'signed-out' });
      assertCurrentSession(generation);
      await onSuccess?.(generation);
      assertCurrentSession(generation);
      await clearSessionCache(client, generation);
      assertCurrentSession(generation);
      return data;
    },
  });
};
